import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import CountryCodePicker from "@/components/base/CountryCodePicker";
import { countryCodes } from "@/mocks/countryCodes";
import { useAuth } from "@/hooks/useAuth";
import { useState } from "react";

export default function LoginPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const { initiateLogin } = useAuth();
  const navigate = useNavigate();

  const [countryCode, setCountryCode] = useState(
    countryCodes.find((c) => c.code === "+20" && c.country === "Egypt") ||
      countryCodes[0]
  );
  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const digits = phone.replace(/\D/g, "");
    if (digits.length < 7) {
      setError(isAr ? "يرجى إدخال رقم هاتف صالح" : "Please enter a valid phone number");
      return;
    }

    setLoading(true);

    // Simulate sending OTP
    setTimeout(() => {
      initiateLogin(digits, countryCode.code);
      setLoading(false);
      navigate("/verify-otp", { state: { mode: "login" } });
    }, 800);
  };

  return (
    <div className="min-h-screen flex flex-col" dir={isAr ? "rtl" : "ltr"}>
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-4 py-20 md:py-28 bg-background-50">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-primary-50 flex items-center justify-center">
              <i className="ri-smartphone-line text-2xl text-primary-500"></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-2">
              {t("auth.welcome_back")}
            </h1>
            <p className="text-sm text-foreground-500">
              {t("auth.login_subtitle")}
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            className="bg-background-50 border border-background-200 rounded-2xl p-6 md:p-8"
          >
            {/* Phone input */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground-700 mb-2">
                {t("auth.phone_number")}
              </label>
              <div className="flex gap-2">
                <CountryCodePicker value={countryCode} onChange={setCountryCode} />
                <input
                  type="tel"
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    setError("");
                  }}
                  placeholder={isAr ? "1XX XXX XXXX" : "(555) 123-4567"}
                  className="flex-1 px-4 py-3 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                  autoComplete="tel"
                />
              </div>
              {error && (
                <p className="mt-2 text-xs text-red-500 flex items-center gap-1">
                  <i className="ri-error-warning-line"></i>
                  {error}
                </p>
              )}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-primary-500 text-white font-semibold rounded-xl hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin"></i>
                  {isAr ? "جاري الإرسال..." : "Sending code..."}
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line"></i>
                  {t("auth.send_code")}
                </>
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-background-200"></div>
              </div>
              <div className="relative flex justify-center">
                <span className="px-3 bg-background-50 text-xs text-foreground-400">
                  {isAr ? "أو" : "or"}
                </span>
              </div>
            </div>

            {/* Register link */}
            <p className="text-center text-sm text-foreground-500">
              {t("auth.no_account_phone")}{" "}
              <Link
                to="/register"
                className="text-primary-600 font-semibold hover:text-primary-700 transition-colors cursor-pointer"
              >
                {t("auth.sign_up")}
              </Link>
            </p>
          </form>
        </div>
      </main>
      <Footer />
    </div>
  );
}